import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Col, Divider, Row } from "antd";

import BeneficiaryGroupContext from "../../../context/beneficiary-groups/context";
import BeneficiaryGroupContextProvider from "../../../context/beneficiary-groups/provider";

import { execute } from "../../../utils/helpers/execute";
import { getTranslations } from "../../../utils/helpers/translations";
import { languages } from "../../../utils/helpers/constants";

import Spin from "../../../components/general/antd/spin";
import FieldBuilder from "../../../components/form-components/field-builder";
import FormItem from "../../../components/general/form-item";
import Controller from "../../../components/form-components/controller";
import ImageUploader from "../../../components/form-components/image-uploader";
import OldImage from "../../../components/form-components/old-image";

import styles from "../../../components/trademark/trademark-form/style.module.scss";

import { MediumFor } from "../../../models/medium/enum";
import { IUpdateBeneficiaryGroup } from "../../../models/beneficiary-groups/request";

const Form = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { details, actions, loading } = useContext(BeneficiaryGroupContext);

    const methods = useForm<IUpdateBeneficiaryGroup>({
        defaultValues: {
            name: getTranslations(details?.translations?.name),
            imageId: details?.image?.id,
        },
    });

    const {
        handleSubmit,
        reset,
    } = methods;

    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        if (id) {
            actions.getDetails(Number(id));
        }
    }, [id]);

    useEffect(() => {
        reset({
            name: getTranslations(details?.translations?.name),
            imageId: details?.image?.id,
        });
    }, [details]);

    const onSubmit = async (data: IUpdateBeneficiaryGroup) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateBeneficiaryGroup(details.id, data)
                    : await actions.createBeneficiaryGroup(data);
                navigate(-1);
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_beneficiary_group") : t("add_beneficiary_group")}
                    </span>
                    <Divider className={styles.divider} />

                    {loading.includes("details") ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {languages.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`name[${lang.code}]`}
                                            label={`${t("name")} (${lang.name})`}
                                            input={{ type: "text" }}
                                            rules={{ required: true }}
                                        />
                                    </Col>
                                ))}

                                

                                <Col span={24}>
                                    <FormItem label={t("image")} required>
                                        <Controller
                                            name="imageId"
                                            rules={{ required: true }}
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                                {details?.image?.url && <OldImage src={details.image.url} />}

                                
                            </Row>
                        </div>
                    )}
                </div>

                <div className={styles.formFooter}>
                    <Button onClick={() => navigate(-1)}>{t("cancel")}</Button>
                    <Button loading={loadingSubmit} htmlType="submit" type="primary">
                        {t("save")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

const BeneficiaryGroupForm = () => {
    return (
        <BeneficiaryGroupContextProvider>
            <Form />
        </BeneficiaryGroupContextProvider>
    );
};

export default BeneficiaryGroupForm;
